import { setzeFarbhinweis } from '@theme/dp-ohne-bild';
import { bildSichtbar, auswahlAusDom } from '@theme/dp-alt-marken';

/**
 * Blendet die Bildergalerie beim Variantenwechsel weich ueber.
 *
 * Wechselt die Farbe, zeichnet das Theme den Abschnitt neu und ersetzt die
 * ganze Galerie - das neue Bild schlug bisher hart ein. Hier wird im Moment
 * des Wechsels ein Standbild der alten Galerie ueber sie gelegt: eine Kopie
 * der bestehenden Bildelemente, deckungsgleich positioniert. Sobald die neue
 * Galerie eingesetzt ist, blendet das Standbild aus - darunter steht schon
 * das neue Bild, und der Wechsel wird zu einer Ueberblendung.
 *
 * Das Standbild haengt am Abschnitt um die Galerie, nicht an der Galerie
 * selbst - die wird ja gerade ausgetauscht. Wird auch der Abschnitt ersetzt,
 * verschwindet das Standbild einfach mit: dann bleibt es beim harten Wechsel,
 * schlimmer als vorher wird es nie.
 *
 * Die Grossansicht hat ihre eigene Ueberblendung (dp-bild-overlay.js); dieses
 * Skript kuemmert sich um die Galerie auf der Seite.
 *
 * Ausserdem traegt es den Farbfilter der Galerie im Browser (filtereNachFarbe,
 * serverseitig dp-bildfilter.liquid). Die Regel: Bilder ohne Farb-Marke im
 * Alt-Text stehen immer. Bilder mit Marke ("farbe:schwarz") erscheinen erst,
 * wenn ihre Farbe gewaehlt ist - ohne gewaehlte Farbe bleiben sie alle
 * verborgen. Eine Ausnahme fuer das erste Bild gibt es nicht: Traegt ein
 * Artikel nur markierte Bilder, steht die Galerie bis zur Farbwahl leer da
 * und haelt dabei ihre Flaeche (Klasse dp-galerie--leer).
 */

/** @type {HTMLElement | null} */
let standbild = null;
/** Die Einzelbilder des Standbilds, aufgeschluesselt nach Platz. */
let standbildTeile = new Map();
/** Die Galerie, die beim Anlegen des Standbilds auf der Seite stand. */
let alteGalerie = null;
/** Ihr erstes Bild - aendert sich die Quelle, wurde an Ort und Stelle getauscht. */
let alteQuelle = '';
/**
 * Die Kachelhoehen der alten Galerie, verzeichnet je Platz im Raster.
 *
 * Entscheidend ist der Platz, nicht das Bild: beim Variantenwechsel sortiert
 * das Theme die Bilder um, und was der Betrachter als Sprung saehe, ist eine
 * Kachel, die ploetzlich woanders liegt oder anders hoch ist. Gemerkt wird
 * deshalb die ganze Lage - Ort und Hoehe im Dokument. Steht am selben Platz
 * hinterher alles gleich, gibt es nichts zu bewegen.
 *
 * Die Galerie rendert mehrere Fassungen parallel (Handy und Rechner); die
 * Plaetze werden deshalb je Elterngruppe gezaehlt, damit sich die Fassungen
 * nicht vermischen.
 */
let alteLagen = new Map();

/**
 * Nummeriert die Kacheln einer Galerie je Elterngruppe durch.
 *
 * @param {Element} galerie
 * @returns {{ kachel: Element, schluessel: string }[]}
 */
function plaetzeVon(galerie) {
  const eltern = new Map();
  let naechsteGruppe = 0;
  const liste = [];

  for (const kachel of galerie.querySelectorAll('.product-media-container')) {
    /*
     * Vom Farbfilter versteckte Kacheln zaehlen nicht mit. Sonst verschiebt
     * jede ausgeblendete Kachel die Nummern aller folgenden, nach einem
     * Farbwechsel findet kaum ein Platz seine alte Lage wieder - und nur die
     * erste Kachel (immer sichtbar, immer Nummer null) wuerde gleiten.
     * So heisst die Zuordnung: n-te sichtbare Kachel alt und neu.
     */
    if (kachel instanceof HTMLElement && kachel.style.display === 'none') continue;
    const gruppe = kachel.parentElement;
    if (!eltern.has(gruppe)) eltern.set(gruppe, { nr: naechsteGruppe++, lauf: 0 });
    const stand = eltern.get(gruppe);
    liste.push({ kachel, schluessel: stand.nr + ':' + stand.lauf++ });
  }

  return liste;
}

/**
 * Der sichtbare Bildkasten einer Kachel.
 *
 * Gemessen wird nie der aeussere Container: er traegt den Spaltenabstand als
 * eigenen Raum und ist damit groesser als das, was man sieht. Ein
 * Schnappschuss in Containergroesse dehnte jedes Bild um genau diesen
 * Abstand - beim Anlegen wirkten alle Bilder kurz herangezoomt und ragten
 * ueber die Kachelraender.
 *
 * @param {Element} kachel
 * @returns {Element}
 */
function bildkastenVon(kachel) {
  return kachel.querySelector('.product-media') || kachel;
}

/**
 * Die gesamte Auswahl des Kunden (alle Optionen des Artikels), aus der Variantenauswahl des Kauffensters und der
 * Produktseite - Schluessel und Werte normalisiert wie die Marken (dp-alt-marken.js). Nicht gewaehlt: fehlt.
 *
 * @returns {Record<string, string>}
 */
function gewaehlteAuswahl() {
  const auswahl = {};
  for (const wurzel of document.querySelectorAll('.dp-kauffenster, variant-picker')) {
    for (const [schluessel, wert] of Object.entries(auswahlAusDom(wurzel))) {
      if (!(schluessel in auswahl)) auswahl[schluessel] = wert;
    }
  }
  return auswahl;
}

/** Die Optionen des Artikels, vom Server am Galerie-Element vermerkt (data-dp-optionen, "|farbe|groesse|"). */
function optionenDerGalerie(galerie) {
  const roh = galerie instanceof HTMLElement ? galerie.dataset.dpOptionen || '' : '';
  return roh.split('|').filter(Boolean);
}

/**
 * Blendet Galeriebilder nach der gewaehlten Farbe ein und aus.
 *
 * Die Zuordnung steht im Alt-Text der Bilder ("farbe:schwarz"). Ein Bild mit
 * einer solchen Marke erscheint nur, wenn seine Farbe gewaehlt ist - ohne
 * gewaehlte Farbe also gar nicht. Bilder ohne Marke gelten fuer alle Farben
 * und bleiben immer stehen. Dieselbe Regel wie dp-bildfilter.liquid beim
 * Seitenaufbau.
 *
 * Frueher blieb das erste Bild einer Gruppe grundsaetzlich stehen, damit die
 * Galerie nie leer ist. Das gilt nicht mehr: eine leere Galerie ist gewollt,
 * sie traegt dann die Klasse dp-galerie--leer und haelt ihre Flaeche
 * (divineprint.css), statt auf null zusammenzufallen.
 *
 * @param {Element | null} galerie
 * @param {boolean} [bewegt] Die Kacheln gleiten an ihre neuen Plaetze, neue blenden ein, gehende aus.
 */
function filtereNachFarbe(galerie, bewegt) {
  if (!galerie) return;
  const vorher = bewegt ? lagenDerKacheln(galerie) : null;
  const gekommen = [];
  const gegangen = [];

  /*
   * Die Auswahl aller Optionen, nicht nur der Farbe: Eine Marke "groesse:xl"
   * wirkt wie "farbe:schwarz" (dp-alt-marken.js, bildSichtbar; serverseitig
   * dp-bild-sichtbar.liquid). Die Farbe allein bleibt die Vorschaufarbe-Logik.
   */
  const auswahl = gewaehlteAuswahl();
  // Optionen mit nur einem Wert (Tasse "Standard") gelten als gewaehlt - vom Server am Galerie-Element vermerkt.
  for (const paar of (galerie instanceof HTMLElement ? galerie.dataset.dpEinzig || '' : '').split('|')) {
    const [schluessel, wert] = paar.split('=');
    if (schluessel && wert && !(schluessel in auswahl)) auswahl[schluessel] = wert;
  }
  const optionen = optionenDerGalerie(galerie);
  const soll = auswahl.farbe || '';
  /*
   * Die Vorschaufarbe (dp-veredelung.js, pflegeVorschaufarbe): Ohne gewaehlte
   * Farbe stehen die Vorschaubilder dieser Farbe da, damit die Vorschau zu
   * sehen ist. Hat die gewaehlte Farbe selbst kein Vorschaubild, kommen die
   * Vorschaubilder der Vorschaufarbe zu ihren Bildern hinzu.
   */
  const wurzel = document.documentElement;
  const vorschau = wurzel.dataset.dpVorschaufarbe || '';
  const vorschauMedien = new Set(String(wurzel.dataset.dpVorschauMedien || '').split(',').filter(Boolean));
  const istVorschaubild = (kachel) => {
    const medium = kachel.querySelector('.product-media[data-media-id]');
    return !!medium && vorschauMedien.has(String(medium.getAttribute('data-media-id')));
  };

  let imRaster = 0;
  let sichtbarImRaster = 0;

  for (const kachel of galerie.querySelectorAll('.product-media-container')) {
    if (!(kachel instanceof HTMLElement)) continue;

    const bild = kachel.querySelector('img');
    // Die Marken, auch neben anderen ("ki, farbe:rot, zoom:30") - dp-alt-marken.js.
    const alt = bild ? bild.alt : '';

    /*
     * Von der Vorschaufarbe kommen nur die Bilder, die wirklich eine Vorschau
     * tragen - auch ohne gewaehlte Farbe. Frueher stand dann die ganze Farbe
     * da, Rueckansicht und Details ohne Schriftzug eingeschlossen (Jimmi,
     * Oktober 2026). Mit gewaehlter Farbe gilt deren volle Galerie.
     */
    const zeigen =
      bildSichtbar(alt, auswahl, optionen) ||
      (!!vorschau && istVorschaubild(kachel) && bildSichtbar(alt, { ...auswahl, farbe: vorschau }, optionen));

    // Nur schreiben, was sich aendert: so loest ein Filterlauf keine neuen
    // Mutationen aus, und der Beobachter unten kann ihn gefahrlos nach jeder
    // Aenderung anstossen, ohne sich selbst in Gang zu halten.
    const neu = zeigen ? '' : 'none';
    if (kachel.style.display !== neu) {
      if (vorher) (zeigen ? gekommen : gegangen).push(kachel);
      kachel.style.display = neu;
    }

    if (kachel.closest('.media-gallery__grid')) {
      imRaster += 1;
      if (zeigen) sichtbarImRaster += 1;
    }
  }

  // Die leere Galerie zeigt den Hinweis "Noch keine Bilder" - der Zustand steht an ihr selbst.
  if (imRaster) galerie.classList.toggle('dp-galerie--leer', sichtbarImRaster === 0);

  // Seine zweite Zeile sagt, ob erst eine Farbe zu waehlen ist oder die gewaehlte keine Bilder hat.
  setzeFarbhinweis(galerie.querySelector('.dp-ohne-bild--farbwahl'), auswahl);

  if (vorher && (gekommen.length || gegangen.length)) bewegeFilterwechsel(galerie, vorher, gekommen, gegangen);
}

/**
 * Die Lage jeder sichtbaren Kachel - vor einem Filterlauf gemessen.
 *
 * @param {Element} galerie
 * @returns {Map<HTMLElement, DOMRect>}
 */
function lagenDerKacheln(galerie) {
  const lagen = new Map();
  for (const kachel of galerie.querySelectorAll('.product-media-container')) {
    if (!(kachel instanceof HTMLElement) || kachel.style.display === 'none') continue;
    const rand = kachel.getBoundingClientRect();
    if (rand.width && rand.height) lagen.set(kachel, rand);
  }
  return lagen;
}

/**
 * Bewegt einen Filterlauf ohne Variantenwechsel - die Vorschaufarbe
 * (dp-veredelung.js) blendet Vorschaubilder ein oder aus, ohne dass das
 * Theme eine neue Galerie liefert. Dort sprang das Bild bisher hart herein
 * (Jimmi, September 2026). Jetzt wie beim Farbwechsel: Die bleibenden
 * Kacheln gleiten an ihre neuen Plaetze, hinzugekommene blenden danach ein,
 * und gehende blenden an ihrem alten Platz aus.
 *
 * @param {Element} galerie
 * @param {Map<HTMLElement, DOMRect>} vorher
 * @param {HTMLElement[]} gekommen
 * @param {HTMLElement[]} gegangen
 */
function bewegeFilterwechsel(galerie, vorher, gekommen, gegangen) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  // Dieselben Werte wie die Fahrten beim Farbwechsel (lasseKachelnGleiten, gibNachzueglerFrei).
  const DAUER = 300;
  const kurve = 'ease-out';

  /*
   * Erst den dichten Satz fuer den neuen Bestand setzen lassen, dann messen.
   * Er kam sonst ein Bild spaeter: Gemessen wurde die alte Lage, nichts
   * bewegte sich, und danach sprangen die Kacheln an ihre neuen Plaetze
   * (Jimmi, September 2026).
   */
  for (const gitter of galerie.querySelectorAll('.media-gallery__grid')) gitter.dispatchEvent(new CustomEvent('dp:satz-jetzt'));

  // Gehende blenden an ihrem alten Platz aus - als Kopie, das Original ist schon verborgen.
  const halter = galerie.closest('.shopify-section') || galerie.parentElement;
  if (halter instanceof HTMLElement && gegangen.length) {
    if (getComputedStyle(halter).position === 'static') halter.style.position = 'relative';
    const halterRand = halter.getBoundingClientRect();
    for (const kachel of gegangen) {
      const rand = vorher.get(kachel);
      if (!rand) continue;
      if (kachel.classList.contains('dp-modell3d')) continue;
      const geist = /** @type {HTMLElement} */ (kachel.cloneNode(true));
      geist.setAttribute('aria-hidden', 'true');
      // Kopierte Zeichenflaechen sind leer, belegen aber Speicher - gleich frei.
      gibLeinwaendeFrei(geist);
      geist.removeAttribute('id');
      for (const el of geist.querySelectorAll('[id]')) el.removeAttribute('id');
      Object.assign(geist.style, {
        display: '',
        position: 'absolute',
        top: rand.top - halterRand.top + 'px',
        left: rand.left - halterRand.left + 'px',
        width: rand.width + 'px',
        height: rand.height + 'px',
        margin: '0',
        pointerEvents: 'none',
        zIndex: '3',
      });
      halter.appendChild(geist);
      geist.animate([{ opacity: 1 }, { opacity: 0 }], { duration: NACHZUEGLER_BLENDE, easing: 'ease-out', fill: 'forwards' }).finished.then(
        () => { geist.remove(); gibLeinwaendeFrei(geist, true); },
        () => { geist.remove(); gibLeinwaendeFrei(geist, true); }
      );
    }
  }

  // Die bleibenden gleiten von ihrer alten Lage an die neue.
  for (const [kachel, alt] of vorher) {
    if (kachel.style.display === 'none') continue;
    const neu = kachel.getBoundingClientRect();
    const dx = alt.left - neu.left;
    const dy = alt.top - neu.top;
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;
    kachel.animate([{ transform: 'translate(' + dx + 'px, ' + dy + 'px)' }, { transform: 'none' }], { duration: DAUER, easing: kurve });
  }

  /*
   * Hinzugekommene blenden ein, wenn die anderen Platz gemacht haben - wie
   * beim Farbwechsel erst nach den Fahrten und einer kurzen Ruhe. Bis dahin
   * stehen sie unsichtbar auf ihrem Platz.
   */
  const bewegtSich = [...vorher.keys()].some((k) => k.getAnimations().length);
  for (const kachel of gekommen) {
    kachel.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: NACHZUEGLER_BLENDE,
      delay: bewegtSich ? DAUER + RUHE_NACH_FAHRT / 2 : 0,
      easing: 'ease-out',
      fill: 'backwards',
    });
  }
}

/**
 * Das erste Bild, das in der Galerie wirklich zu sehen ist - verborgene
 * Kacheln (Farbfilter) laden ihr Bild nie und taugen nicht zum Abwarten.
 *
 * @param {Element | null} galerie
 * @returns {HTMLImageElement | null}
 */
function erstesSichtbaresBild(galerie) {
  if (!galerie) return null;
  // Das Raster ist die sichtbare Fassung; die Diaschau des Themes steht verborgen davor.
  const bereich = galerie.querySelector('.media-gallery__grid') || galerie;
  for (const bild of bereich.querySelectorAll('.product-media img')) {
    const kachel = bild.closest('.product-media-container');
    if (kachel instanceof HTMLElement && kachel.style.display === 'none') continue;
    if (bild instanceof HTMLImageElement) return bild;
  }
  return null;
}

/*
 * Stoesst einen Filterlauf fuer die naechste Bildwiederholung an - gesammelt,
 * egal wie viele Aenderungen ihn anfordern.
 *
 * Gebraucht wird das gegen einen Wettlauf beim schnellen Durchklicken der
 * Farben: Trifft die Antwort eines frueheren Wechsels erst ein, wenn das
 * Standbild des spaeteren schon abgeraeumt ist, setzte das Theme eine
 * ungefilterte Galerie ein und niemand filterte nach - trotz gewaehlter
 * Farbe standen wieder alle Bilder da.
 */
let filterGeplant = false;
/* Der naechste Lauf kommt von der Vorschaufarbe - er darf sich bewegen. */
let filterBewegt = false;

/*
 * Gilt eine neue Vorschaufarbe, filtert die Galerie neu - mit Bewegung,
 * solange kein Variantenwechsel laeuft (der hat seine eigene). Danach
 * zeichnet dp-veredelung.js die Personalisierung in die neu sichtbaren
 * Bilder (dp:galerie-gefiltert).
 */
document.addEventListener('dp:vorschaufarbe', () => {
  filterBewegt = true;
  planeFilter();
});

function planeFilter() {
  if (filterGeplant) return;
  filterGeplant = true;

  const lauf = () => {
    if (!filterGeplant) return;
    filterGeplant = false;
    const bewegt = filterBewegt && !standbild;
    filterBewegt = false;
    const galerie = sichtbareGalerie();
    filtereNachFarbe(galerie, bewegt);
    if (bewegt) document.dispatchEvent(new CustomEvent('dp:galerie-gefiltert'));

    /*
     * Gleich im Anschluss: laeuft gerade ein Wechsel, werden die
     * hinzugekommenen Kacheln aus dem Bild genommen. Frueher geschah das
     * erst, wenn das Theme seine Antwort ausgeliefert hatte - bis dahin
     * stand die neue Galerie schon vollstaendig da, und was ueber das
     * Standbild hinausragte, war zu sehen: ein schwarzes Shirt unter einer
     * gruenen Kachel.
     */
    if (standbild) haltNachzuegler(galerie);
  };

  // Beide Wege, der erste gewinnt: im verdeckten Tab steht die
  // Bildwiederholung still, dort uebernimmt die Zeitschaltung.
  requestAnimationFrame(lauf);
  window.setTimeout(lauf, 200);
}

let sicherung = 0;

/*
 * Die Marke "es wird gewechselt" haelt das Kauffenster zurueck
 * (dp-kauffenster.js). Sie faellt, sobald die Kachelfahrten abgeraeumt
 * sind - dann stehen Groesse und Lage der Kacheln fest, und mehr braucht
 * das Fenster nicht zu wissen. Ob die Bilder in den Kacheln schon geladen
 * sind, aendert daran nichts.
 *
 * Der Wecker ist nur das Netz: kommt es zu keiner Fahrt, faellt die Marke
 * trotzdem.
 */
const MARKE_NETZ = 2600;
let wechselMarke = 0;

function loeseWechselmarke() {
  window.clearTimeout(wechselMarke);
  delete document.documentElement.dataset.dpGalerieWechselt;
}

/*
 * Ein abgeraeumtes Standbild gibt den Speicher seiner Zeichenflaechen sofort
 * ab (Personalisierung, letztes Bild der 3D-Szene) - Safari am iPhone zaehlt
 * sie sonst weiter, bis der Speicherbereiniger kommt, und beendete die Seite
 * nach einigen Farbwechseln (Jimmi, Oktober 2026).
 */
function gibLeinwaendeFrei(wurzel, mitBildern = false) {
  if (!(wurzel instanceof Element)) return;
  // Beim Abraeumen auch die Bilder: ohne Adresse gibt Safari ihre entschluesselte Fassung frei (Speicherspur am iPhone, Oktober 2026).
  if (mitBildern) for (const b of wurzel instanceof HTMLImageElement ? [wurzel] : wurzel.querySelectorAll('img')) {
    b.removeAttribute('srcset');
    b.removeAttribute('src');
  }
  for (const c of wurzel.querySelectorAll('canvas')) {
    // 1 x 1 und geleert - so gibt Safari den Speicher verlaesslich ab (siehe gibLeinwandFrei in dp-veredelung.js).
    c.width = 1;
    c.height = 1;
    try {
      const k = c.getContext('2d');
      if (k) k.clearRect(0, 0, 1, 1);
    } catch {
      /* eine WebGL-Flaeche hat keinen 2D-Zusammenhang - dann reicht die Groesse */
    }
  }
}

function raeumeStandbildAb() {
  if (!standbild) return;

  const bild = standbild;
  standbild = null;
  standbildTeile = new Map();
  window.clearTimeout(sicherung);

  // Die Bilderflaeche darf wieder ihre eigene Hoehe annehmen.
  delete document.documentElement.dataset.dpGalerieHaelt;

  haltePillenRest(bild);
  bild.style.opacity = '0';
  window.setTimeout(() => {
    bild.remove();
    gibLeinwaendeFrei(bild, true);
  }, 300);

}

/*
 * Die Pille "Vorschau" ueberdauert das Standbild.
 *
 * Die KI-Pille kommt mit der neuen Galerie und steht laengst, wenn das
 * Standbild ausblendet. Die Vorschau-Pille dagegen setzt IMPRINT erst,
 * wenn es das neue Bild gezeichnet hat (dp-veredelung.js) - oft eine, zwei
 * Sekunden spaeter. Ihre Kopie aus dem Standbild bleibt deshalb auf einer
 * eigenen Schicht stehen, bis die echte einblendet; dann blendet die Kopie
 * im selben Takt aus. Kommt keine, geht die Kopie nach PILLEN_REST_HOECHSTENS.
 *
 * Gewartet wird ueber das Ereignis, mit dem die echte Pille ihr Einblenden
 * beginnt (transitionrun) - kein Beobachter, kein Lauf in jeder
 * Bildwiederholung, an der Galerie wird nichts veraendert.
 */
const PILLEN_REST_HOECHSTENS = 2500;
/** @type {HTMLElement | null} */
let pillenRest = null;
let pillenRestWecker = 0;

/** Nimmt die Schicht mit den Pillenkopien weg - weich im Takt der Pillen oder sofort. */
function raeumePillenRestAb(weich) {
  const rest = pillenRest;
  if (!rest) return;
  pillenRest = null;
  window.clearTimeout(pillenRestWecker);
  document.removeEventListener('transitionrun', vorschauKommt, true);
  if (!weich) {
    rest.remove();
    return;
  }
  rest.style.transition = 'opacity var(--dp-duration) var(--dp-ease)';
  rest.style.opacity = '0';
  window.setTimeout(() => rest.remove(), 600);
}

/** @param {TransitionEvent} ereignis */
function vorschauKommt(ereignis) {
  const ziel = ereignis.target;
  if (ereignis.propertyName !== 'opacity' || !(ziel instanceof Element)) return;
  if (!ziel.classList.contains('dp-kachel-marke--live') || !ziel.closest('media-gallery')) return;
  raeumePillenRestAb(true);
}

/**
 * Hebt die Vorschau-Pillen eines Standbilds, das gleich geht, auf die
 * eigene Schicht.
 *
 * @param {HTMLElement} bild
 */
function haltePillenRest(bild) {
  const galerie = sichtbareGalerie();
  const echteDa =
    !!galerie &&
    [...galerie.querySelectorAll('.dp-kachel-marke--live:not(.dp-kachel-marke--ruht)')].some((p) => p.getClientRects().length > 0);
  if (echteDa) {
    raeumePillenRestAb(true);
    return;
  }

  const lebend = [...bild.querySelectorAll('.dp-kachel-marken')].filter((m) => m.querySelector('.dp-kachel-marke--live'));
  // Nichts Neues zu halten - eine Schicht vom vorigen Wechsel bleibt einfach stehen.
  if (!lebend.length || !bild.parentElement) return;
  raeumePillenRestAb(false);

  const rest = document.createElement('div');
  rest.setAttribute('aria-hidden', 'true');
  rest.style.position = 'absolute';
  rest.style.top = bild.style.top;
  rest.style.left = bild.style.left;
  rest.style.width = bild.style.width;
  rest.style.height = bild.style.height;
  rest.style.margin = '0';
  rest.style.pointerEvents = 'none';
  rest.style.zIndex = '3';
  for (const marken of lebend) {
    // Nur die Vorschau: Die KI-Pille steht schon echt darunter, doppelt laege sie dunkler.
    for (const pille of marken.querySelectorAll('.dp-kachel-marke:not(.dp-kachel-marke--live)')) pille.remove();
    rest.appendChild(marken);
  }
  bild.parentElement.appendChild(rest);
  pillenRest = rest;
  document.addEventListener('transitionrun', vorschauKommt, true);
  pillenRestWecker = window.setTimeout(() => raeumePillenRestAb(true), PILLEN_REST_HOECHSTENS);
}

/**
 * Die Galerie, die der Besucher tatsaechlich vor sich hat.
 *
 * Im Dokument koennen mehrere media-gallery-Elemente stehen: das Theme
 * rendert Fassungen fuer verschiedene Breiten, und beim Variantenwechsel
 * bleibt die abgeloeste Fassung einen Moment liegen - auf einem Geraet
 * waren es vier gleichzeitig. Ein blindes querySelector greift dann die
 * erste, und das ist eine unsichtbare oder veraltete: Gemessen wurde die
 * falsche Galerie, die Kachellagen passten hinterher zu nichts, und es
 * fuhr keine einzige Kachel.
 *
 * Genommen wird deshalb die letzte, die wirklich dargestellt wird - die
 * zuletzt eingesetzte ist die neue.
 *
 * @returns {Element | null}
 */
function sichtbareGalerie() {
  const alle = [...document.querySelectorAll('media-gallery')];
  for (let i = alle.length - 1; i >= 0; i -= 1) {
    const kandidat = alle[i];
    if (!(kandidat instanceof HTMLElement)) continue;
    // offsetParent faellt bei display:none weg; die Masse schliessen
    // ausgeblendete und noch leere Fassungen aus.
    if (kandidat.offsetParent === null && getComputedStyle(kandidat).position !== 'fixed') continue;
    const rand = kandidat.getBoundingClientRect();
    if (rand.width && rand.height) return kandidat;
  }
  return alle[0] || null;
}

/** Die Farbe, fuer die das liegende Standbild gelegt wurde. */
let standbildFarbe = null;

/**
 * Der Haarstrich der Galeriebilder (media-gallery .product-media::after, divineprint.css) als eigene Flaeche in einer Kopie. Die Kopien des
 * Standbilds leben ausserhalb der Galerie, die Stilblatt-Regel erreicht sie nicht - ohne diese Kante ging der Rand der 3D-Kachel (und der
 * Vorlagen-Kachel) fuer die Dauer des Wechsels aus. Die Farbe kommt vom echten Rand (hell wie dunkel).
 */
/**
 * Die Farbe des echten Haarstrichs (Vordergrundfarbe mit 9 Prozent: am hellen Grund dunkel, am dunklen hell). Die Kopien des Wechsels
 * trugen bisher fest ein dunkles Grau - am dunklen Grund unsichtbar: Beim Farbwechsel verloren alle Kacheln ihre Umrandung.
 */
function haarstrichFarbe() {
  const medien = document.querySelector('media-gallery .product-media');
  const ring = medien ? getComputedStyle(medien, '::after').boxShadow : '';
  const treffer = /rgba?\([^)]*\)/.exec(ring || '');
  return treffer ? treffer[0] : 'rgb(10 10 10 / 0.09)';
}

function haengeHaarstrichAn(teil, medien) {
  if (!(medien instanceof Element)) return;
  const ring = getComputedStyle(medien, '::after').boxShadow;
  if (!ring || ring === 'none') return;
  const kante = document.createElement('div');
  kante.style.cssText = 'position:absolute;inset:0;border-radius:inherit;pointer-events:none;z-index:9;';
  kante.style.boxShadow = ring;
  teil.appendChild(kante);
}

function legeStandbild() {
  /*
   * Ein Wechsel, nicht zwei.
   *
   * Das Theme zeichnet beim Variantenwechsel auch die Auswahl neu, und dabei
   * meldet sie ein zweites Mal "geaendert" - dieselbe Farbe, dieselbe
   * Handlung. Frueher begann damit ein zweiter Wechsel: Das Standbild wurde
   * ersetzt, die Lagen an der schon neuen Galerie gemessen, und die
   * zurueckgehaltenen Kacheln kamen frei, kurz bevor die eigentlichen
   * Fahrten losliefen. Genau das sah man als Bild, das auftaucht und gleich
   * darauf umspringt.
   *
   * Ein wirklicher zweiter Wechsel traegt eine andere Farbe und kommt
   * weiterhin durch.
   */
  // Dieselbe Auswahl noch einmal gemeldet (alle Optionen, nicht nur die Farbe) ist kein neuer Wechsel.
  const farbe = JSON.stringify(Object.entries(gewaehlteAuswahl()).sort());
  if (standbild && farbe !== '[]' && farbe === standbildFarbe) {
    return;
  }
  standbildFarbe = farbe;

  const galerie = sichtbareGalerie();
  if (!galerie) {
    return;
  }
  alteGalerie = galerie;

  const erstes = galerie.querySelector('.product-media img');
  alteQuelle = erstes ? erstes.currentSrc || erstes.src : '';

  /*
   * Nur gemessene Hoehen kommen ins Verzeichnis: ausgeblendete Fassungen und
   * Kacheln ausserhalb des gerenderten Bereichs melden null und bleiben
   * aussen vor - fuer sie laesst sich hinterher kein ehrlicher Startwert
   * angeben.
   */
  alteLagen = new Map();
  for (const eintrag of plaetzeVon(galerie)) {
    const rand = bildkastenVon(eintrag.kachel).getBoundingClientRect();
    if (!rand.height) continue;
    // Dokumentkoordinaten: so stimmen die Wege auch, falls dazwischen gescrollt wird.
    const altesBild = eintrag.kachel.querySelector('img');
    alteLagen.set(eintrag.schluessel, {
      oben: rand.top + window.scrollY,
      links: rand.left + window.scrollX,
      hoehe: rand.height,
      breite: rand.width,
      quelle: altesBild ? (altesBild.currentSrc || altesBild.src).split('?')[0] : '',
    });
  }

  const halter = galerie.closest('.shopify-section') || galerie.parentElement;
  if (!(halter instanceof HTMLElement)) {
    return;
  }

  // Ein aelteres Standbild ist damit ueberholt.
  if (standbild) {
    window.clearTimeout(sicherung);
    // Seine Vorschau-Pille bleibt stehen - das neue Standbild faende nur die noch ruhende echte.
    haltePillenRest(standbild);
    standbild.remove();
    gibLeinwaendeFrei(standbild, true);
    standbild = null;
  }

  const halterRand = halter.getBoundingClientRect();
  const rand = galerie.getBoundingClientRect();
  if (!rand.width || !rand.height) {
    return;
  }

  /*
   * Die Bilderflaeche behaelt fuer die Dauer des Wechsels ihre Hoehe.
   *
   * Beim Einsetzen der neuen Galerie steht das Raster fuer ein einziges
   * Bild anders da - eine Kachel nimmt kurz die volle Breite ein und ist
   * damit doppelt so hoch. Alles darunter, die Reihe "Fuer dich" etwa,
   * sprang dabei um ihre Hoehe nach unten und sofort wieder zurueck.
   *
   * Waehrend das Standbild liegt, sieht ohnehin niemand die Galerie
   * darunter; sie darf also stillhalten. Freigegeben wird sie mit dem
   * Standbild, und die neue Hoehe kommt dann in einem Schritt.
   */
  const wurzel = document.documentElement;
  wurzel.style.setProperty('--dp-galerie-halt', Math.round(rand.height) + 'px');
  wurzel.dataset.dpGalerieHaelt = '';

  if (getComputedStyle(halter).position === 'static') {
    halter.style.position = 'relative';
  }

  /*
   * Das Standbild ist ein flacher Schnappschuss, kein lebender Klon.
   *
   * Ein geklonter Galeriebaum legte sich sein Layout selbst neu - in einem
   * anderen Umfeld (anderer Elternkasten, andere Containermasse) kamen dabei
   * andere Kachelgroessen heraus, und die Kopie lag versetzt ueber dem
   * Original: in den Fugen zwischen den echten Kacheln schimmerten fremde
   * Bilder durch. Hier wird stattdessen jedes sichtbare Bild einzeln an
   * seine exakt gemessene Stelle gelegt - da rechnet nichts, da verrutscht
   * nichts.
   */
  const kopie = document.createElement('div');
  kopie.setAttribute('aria-hidden', 'true');

  /*
   * Das Standbild bekommt den Seitengrund als eigene, deckende Flaeche.
   *
   * Ohne sie war es nur eine Sammlung von Bildern mit Luecken dazwischen -
   * und durch diese Luecken schien die neue Galerie, die darunter laengst an
   * ihren Endplaetzen steht. Mit dem Grund zeigt das Standbild die alte
   * Szene vollstaendig, samt ihrer leeren Stellen; die neue erscheint erst
   * mit der Blende und mit den Fahrten, nicht vorab durch die Fugen.
   */
  const grundgeber = document.body;
  const grundStil = getComputedStyle(grundgeber);
  const grund = grundStil.backgroundColor;
  kopie.style.backgroundColor = grund && grund !== 'rgba(0, 0, 0, 0)' ? grund : '#fff';
  /*
   * Samt der Textur der Seite (Jimmi, Oktober 2026): Der Body traegt ueber
   * seiner Farbe ein feines Rauschen, am Bildschirm verankert. Mit der Farbe
   * allein stand das Standbild als helles Viereck hinter der Galerie, bis
   * die neuen Bilder geladen waren. Am Bildschirm verankert liegt die Textur
   * deckungsgleich auf der der Seite.
   */
  if (grundStil.backgroundImage && grundStil.backgroundImage !== 'none') {
    kopie.style.backgroundImage = grundStil.backgroundImage;
    kopie.style.backgroundSize = grundStil.backgroundSize;
    kopie.style.backgroundRepeat = grundStil.backgroundRepeat;
    kopie.style.backgroundPosition = grundStil.backgroundPosition;
    kopie.style.backgroundAttachment = grundStil.backgroundAttachment;
  }

  kopie.style.position = 'absolute';
  kopie.style.top = rand.top - halterRand.top + 'px';
  kopie.style.left = rand.left - halterRand.left + 'px';
  kopie.style.width = rand.width + 'px';
  kopie.style.height = rand.height + 'px';
  kopie.style.margin = '0';
  kopie.style.pointerEvents = 'none';
  kopie.style.zIndex = '2';
  kopie.style.transition = 'opacity 260ms ease-out';

  standbildTeile = new Map();
  for (const { kachel, schluessel } of plaetzeVon(galerie)) {
    const teilRand = bildkastenVon(kachel).getBoundingClientRect();
    if (!teilRand.height || !teilRand.width) continue;
    /*
     * Die 3D-Kachel (Jimmi, Oktober 2026: "Ist es moeglich, die alte Farbe
     * anzuzeigen, den grossen Ladering wieder darueberzulegen und zu warten,
     * bis die neue Farbe geladen hat"): Ihr einziges Bild ist das Titelbild -
     * als Kopie stand es schwarz und beschnitten im Standbild. Stattdessen
     * das letzte Bild der laufenden Szene (buehne.standbild) und darueber,
     * wenn das Warten laenger dauert, der Ladering. Laeuft keine Szene, bleibt
     * die Kachel aus dem Standbild - darunter steht die echte.
     */
    if (kachel.classList.contains('dp-modell3d')) {
      const stufe = [...kachel.querySelectorAll('.dp-modell3d__buehne')].find((x) => x.dpModell3d);
      const buehne = stufe && stufe.dpModell3d && stufe.dpModell3d.buehne;
      /*
       * Nur eine vom Kunden gestartete (sichtbare) Szene liefert ihr letztes Bild samt Ladering. Im Standbild-Zustand (3D nicht gestartet - auch die
       * unsichtbare Szene fuer das Standbild mit Aufdruck) waere ihr Bild eine fremde Pose und der Ring ein Ladezustand, den es dort nie geben darf
       * (Jimmi, 22.10.2026): dann liegt das Standbild der Kachel selbst als flaches Bild ueber der Galerie, ohne Ring.
       */
      const sichtbareSzene = !!(buehne && typeof buehne.standbild === 'function' && stufe.dpModell3d && !stufe.dpModell3d.still && !stufe.classList.contains('dp-modell3d__buehne--still'));
      const stufenRand = (stufe || kachel.querySelector('.dp-modell3d__buehne') || bildkastenVon(kachel)).getBoundingClientRect();
      let ebene = null;
      if (sichtbareSzene) {
        try {
          ebene = buehne.standbild();
        } catch {
          ebene = null;
        }
        if (!ebene) continue;
      } else {
        const ruhe = kachel.querySelector('.dp-modell3d__buehne');
        const quelle = ruhe && (ruhe.querySelector('.dp-modell3d__vorab--aufdruck') || ruhe.querySelector('.dp-modell3d__vorab'));
        if (!(quelle instanceof HTMLImageElement) || !(quelle.currentSrc || quelle.src)) continue;
        const qs = getComputedStyle(quelle);
        ebene = document.createElement('img');
        ebene.decoding = 'sync';
        ebene.alt = '';
        ebene.src = quelle.currentSrc || quelle.src;
        ebene.style.objectFit = qs.objectFit;
        ebene.style.boxSizing = 'border-box';
        ebene.style.padding = qs.padding;
        ebene.style.transform = quelle.style.transform || '';
        ebene.style.maxWidth = 'none';
        ebene.style.pointerEvents = 'none';
      }
      const teil = document.createElement('div');
      // Mit der Szene der Kachel als Grund (Verlauf, Waldsaum) - der Hintergrund bleibt durchgehend stehen (Jimmi, Oktober 2026).
      teil.className = 'dp-galerie-standbild-3d dp-modell3d__flaeche';
      teil.style.position = 'absolute';
      teil.style.top = teilRand.top - rand.top + 'px';
      teil.style.left = teilRand.left - rand.left + 'px';
      teil.style.width = teilRand.width + 'px';
      teil.style.height = teilRand.height + 'px';
      const medien = kachel.querySelector('.product-media');
      if (medien) teil.style.borderRadius = getComputedStyle(medien).borderRadius;
      ebene.style.position = 'absolute';
      ebene.style.top = stufenRand.top - teilRand.top + 'px';
      ebene.style.left = stufenRand.left - teilRand.left + 'px';
      ebene.style.width = stufenRand.width + 'px';
      ebene.style.height = stufenRand.height + 'px';
      ebene.style.zIndex = '4';
      teil.appendChild(ebene);
      /*
       * Die Pille bleibt stehen (Jimmi, 22.10.2026: "die 3D-Vorschau-Pille verschwindet beim Farbwechsel kurz"): Das Standbild der alten Galerie
       * deckt die Kachel samt Pille zu - darum liegt eine rein visuelle Kopie der Pille (gleiche Lage, Masse und Zustand, nicht bedienbar) darueber,
       * bis die neue Galerie da ist. Darunter steht an derselben Stelle schon die Platzhalter-Pille der frischen Kachel.
       */
      const echtePille = kachel.querySelector('.dp-modell3d__morph');
      if (echtePille instanceof HTMLElement) {
        const pr = echtePille.getBoundingClientRect();
        if (pr.width > 0 && pr.height > 0) {
          const klon = /** @type {HTMLElement} */ (echtePille.cloneNode(true));
          klon.setAttribute('aria-hidden', 'true');
          klon.setAttribute('inert', '');
          klon.style.position = 'absolute';
          klon.style.left = pr.left - teilRand.left + 'px';
          klon.style.top = pr.top - teilRand.top + 'px';
          klon.style.bottom = 'auto';
          klon.style.width = pr.width + 'px';
          klon.style.height = pr.height + 'px';
          klon.style.zIndex = '6';
          klon.style.pointerEvents = 'none';
          klon.style.transition = 'none';
          const originale = [...echtePille.children];
          [...klon.children].forEach((kind, i) => {
            if (!(kind instanceof HTMLElement) || !originale[i]) return;
            const cs = getComputedStyle(originale[i]);
            kind.style.opacity = cs.opacity;
            kind.style.visibility = cs.visibility;
            kind.style.transition = 'none';
            kind.tabIndex = -1;
          });
          teil.appendChild(klon);
        }
      }
      /* Auch die Pille "Vorschau" oben links (wenn sie gerade steht) bleibt im Overlay stehen: gleiche Lage und Masse, rein visuell. */
      const vorschau = kachel.querySelector('.dp-kachel-marken > .dp-kachel-marke--live:not(.dp-kachel-marke--ruht)');
      if (vorschau instanceof HTMLElement) {
        const vr = vorschau.getBoundingClientRect();
        if (vr.width > 0 && vr.height > 0) {
          const vk = /** @type {HTMLElement} */ (vorschau.cloneNode(true));
          vk.setAttribute('aria-hidden', 'true');
          vk.classList.add('dp-kachel-marke--klon');
          vk.style.cssText = 'position:absolute;margin:0;transition:none;pointer-events:none;z-index:7;left:' + (vr.left - teilRand.left) + 'px;top:' + (vr.top - teilRand.top) + 'px;opacity:1;box-sizing:border-box';
          /* Keine feste Breite: Die Kopie waechst wie die echte Pille um den Ladering (html.dp-vorschau-arbeitet), sonst stuende "Vorsch..." mit abgeschnittenem Ring da. */
          teil.appendChild(vk);
        }
      }
      haengeHaarstrichAn(teil, medien);
      kopie.appendChild(teil);
      standbildTeile.set(schluessel, teil);
      continue;
    }
    /*
     * Die Vorlagen-Kachel (Jimmi, Oktober 2026: "wenn ich die Farbe aendere, wechselt das Bild der Vorlagen durch irgendein Produktbild"):
     * Ihr <img> ist nur ein unsichtbarer Traeger (opacity 0), die Mauer besteht aus Hintergrundbildern. Als einfache Bildkopie stand dort
     * waehrend des Wechsels ein fremdes Foto. Stattdessen eine flache Kopie der Mauer: jede gezeigte Karte als Hintergrundbild an ihrer
     * gemessenen Stelle, dazu Verlauf, Pille und Haarstrich - nichts rechnet, nichts springt.
     */
    if (kachel.matches('[data-dp-vorlagen-kachel]')) {
      const flaeche = kachel.querySelector('.dp-vorlagen-kachel__flaeche') || bildkastenVon(kachel);
      const minis = [...kachel.querySelectorAll('.dp-vorlagen-mini:not(.dp-vorlagen-mini--weg)')].filter((m) => {
        const b = m.querySelector('.dp-vorlagen-mini__bild');
        return b instanceof HTMLElement && getComputedStyle(b).backgroundImage !== 'none';
      });
      if (minis.length) {
        const teil = document.createElement('div');
        teil.className = 'dp-galerie-standbild-vorlagen';
        const fs = getComputedStyle(flaeche);
        teil.style.cssText =
          'position:absolute;overflow:hidden;top:' + (teilRand.top - rand.top) + 'px;left:' + (teilRand.left - rand.left) + 'px;width:' + teilRand.width + 'px;height:' + teilRand.height + 'px;';
        teil.style.backgroundColor = fs.backgroundColor;
        teil.style.borderRadius = fs.borderRadius;
        for (const m of minis) {
          const b = m.querySelector('.dp-vorlagen-mini__bild');
          const mr = m.getBoundingClientRect();
          if (!(b instanceof HTMLElement) || mr.width <= 0 || mr.height <= 0) continue;
          const ms = getComputedStyle(m);
          const bs = getComputedStyle(b);
          const k = document.createElement('div');
          k.style.cssText =
            'position:absolute;box-sizing:border-box;overflow:hidden;left:' + (mr.left - teilRand.left) + 'px;top:' + (mr.top - teilRand.top) + 'px;width:' + mr.width + 'px;height:' + mr.height + 'px;';
          k.style.borderRadius = ms.borderRadius;
          k.style.backgroundColor = ms.backgroundColor;
          const bi = document.createElement('div');
          bi.style.cssText = 'position:absolute;inset:0;background-repeat:no-repeat;background-position:center;background-size:contain;';
          bi.style.backgroundImage = bs.backgroundImage;
          bi.style.opacity = bs.opacity;
          k.appendChild(bi);
          teil.appendChild(k);
        }
        if (kachel.classList.contains('dp-vorlagen-kachel--satz')) {
          const verlauf = document.createElement('div');
          verlauf.style.cssText =
            'position:absolute;inset:0;z-index:1;border-radius:inherit;pointer-events:none;background:linear-gradient(to bottom, rgb(0 0 0 / 0.34) 0, rgb(0 0 0 / 0) 32%, rgb(0 0 0 / 0) 68%, rgb(0 0 0 / 0.34) 100%);';
          teil.appendChild(verlauf);
        }
        const knopf = kachel.querySelector('.dp-vorlagen-knopf');
        if (knopf instanceof HTMLElement) {
          const kr = knopf.getBoundingClientRect();
          if (kr.width > 0 && kr.height > 0) {
            const klon = /** @type {HTMLElement} */ (knopf.cloneNode(true));
            klon.setAttribute('aria-hidden', 'true');
            klon.setAttribute('inert', '');
            klon.tabIndex = -1;
            klon.style.cssText =
              'position:absolute;margin:0;inset:auto;transition:none;pointer-events:none;z-index:6;left:' + (kr.left - teilRand.left) + 'px;top:' + (kr.top - teilRand.top) + 'px;width:' + kr.width + 'px;height:' + kr.height + 'px;';
            teil.appendChild(klon);
          }
        }
        haengeHaarstrichAn(teil, kachel.querySelector('.product-media'));
        kopie.appendChild(teil);
        standbildTeile.set(schluessel, teil);
        continue;
      }
    }
    const bild = kachel.querySelector('img');
    if (!bild || !(bild.currentSrc || bild.src)) continue;

    const abzug = document.createElement('img');
    /*
     * Sofort entschluesseln, nicht erst im Hintergrund. Safari zeichnet
     * ein frisch angelegtes Bild - auch eines, das laengst im Speicher
     * liegt - erst ein, zwei Bilder spaeter: das Standbild stand so lange
     * als leere weisse Flaeche da, und auf dem iPhone blitzte die Galerie
     * bei jedem Wechsel kurz weiss auf. Chrome zeichnet aus dem Speicher
     * ohne Verzug, dort aendert die Angabe nichts.
     */
    abzug.decoding = 'sync';
    abzug.loading = 'eager';
    abzug.src = bild.currentSrc || bild.src;
    abzug.alt = '';
    abzug.style.position = 'absolute';
    abzug.style.top = teilRand.top - rand.top + 'px';
    abzug.style.left = teilRand.left - rand.left + 'px';
    abzug.style.width = teilRand.width + 'px';
    abzug.style.height = teilRand.height + 'px';
    abzug.style.objectFit = 'cover';
    // Der Fokuspunkt des Originals wandert mit - sonst spraenge der
    // Beschnitt im Standbild auf die Mitte um.
    abzug.style.objectPosition = getComputedStyle(bild).objectPosition;
    abzug.style.maxWidth = 'none';
    /*
     * Derselbe Haarstrich wie auf den echten Galeriebildern. Die Kopien
     * leben ausserhalb der Galerie, die Stilblatt-Regel erreicht sie nicht -
     * ohne eigene Kante ging die Umrandung fuer die Dauer des Wechsels aus
     * und danach wieder an.
     *
     * Als Outline, nicht als Border: eine Border mit border-box drueckte den
     * Bildinhalt um ein Pixel je Seite nach innen - beim Anlegen des
     * Standbilds zoomten dadurch alle Bilder sichtbar einen Hauch heraus.
     * Die Outline liegt an derselben Stelle ueber dem Bild, ohne es zu
     * verkleinern. (Ein Innenschatten schiede aus: er zeichnet unter den
     * Bildpixeln und waere unsichtbar.)
     */
    abzug.style.outline = '1px solid ' + haarstrichFarbe();
    abzug.style.outlineOffset = '-1px';

    const medien = kachel.querySelector('.product-media');
    if (medien) abzug.style.borderRadius = getComputedStyle(medien).borderRadius;

    kopie.appendChild(abzug);
    standbildTeile.set(schluessel, abzug);

    /*
     * Die Personalisierung gehoert ins Standbild (Jimmi, Oktober 2026): Sie
     * liegt als eigene Zeichenflaeche ueber dem Foto (dp-veredelung.js,
     * dp-veredelung__flaeche). Ohne sie verschwand sie beim Farbwechsel
     * sofort, waehrend das alte Foto noch Sekunden stand. Jetzt bleibt sie
     * auf dem alten Bild, bis das neue da ist, und blendet mit ihm aus.
     */
    const leinwand = kachel.querySelector('canvas.dp-veredelung__flaeche');
    const leinwandRand = leinwand instanceof HTMLCanvasElement ? leinwand.getBoundingClientRect() : null;
    if (leinwand instanceof HTMLCanvasElement && leinwandRand && leinwandRand.width && leinwand.width && leinwand.height) {
      const stil = getComputedStyle(leinwand);
      if (stil.display !== 'none' && stil.visibility !== 'hidden' && Number(stil.opacity) > 0) {
        const ebene = document.createElement('canvas');
        ebene.width = leinwand.width;
        ebene.height = leinwand.height;
        const stift = ebene.getContext('2d');
        if (stift) {
          stift.drawImage(leinwand, 0, 0);
          ebene.style.position = 'absolute';
          ebene.style.top = leinwandRand.top - rand.top + 'px';
          ebene.style.left = leinwandRand.left - rand.left + 'px';
          ebene.style.width = leinwandRand.width + 'px';
          ebene.style.height = leinwandRand.height + 'px';
          ebene.style.objectFit = stil.objectFit;
          ebene.style.opacity = stil.opacity;
          ebene.style.borderRadius = abzug.style.borderRadius;
          kopie.appendChild(ebene);
        }
      }
    }

    /*
     * Die Pillen ("KI", "Vorschau") gehoeren auch ins Standbild (Jimmi,
     * Oktober 2026). Ohne sie stand das alte Bild bis zu zwei Sekunden ohne
     * seine Pille da. Einmal kopiert beim Anlegen, an die gemessene Stelle,
     * und mit dem Standbild wieder weg - bewusst ohne weitere Logik: Ein
     * erster Anlauf hielt die Pillen ueber einen Lauf in jeder
     * Bildwiederholung und setzte dabei laufend Klassen an den echten
     * Pillen. Das weckte die Beobachter der Galerie (dp-galerie-satz.js) in
     * jedem Bild - der Ladering ruckelte, die neuen Bilder kamen nicht.
     * Hier wird an der echten Galerie nichts angefasst.
     */
    const marken = kachel.querySelector('.dp-kachel-marken');
    const markenRand = marken ? marken.getBoundingClientRect() : null;
    if (
      marken &&
      markenRand &&
      markenRand.width &&
      markenRand.height &&
      // Nur, was in der Galerie zu sehen ist - sonst stuende die Kopie neben ihr.
      markenRand.left >= rand.left - 1 &&
      markenRand.right <= rand.right + 1 &&
      markenRand.top >= rand.top - 1 &&
      markenRand.bottom <= rand.bottom + 1
    ) {
      const abzugMarken = /** @type {HTMLElement} */ (marken.cloneNode(true));
      for (const pille of abzugMarken.querySelectorAll('.dp-kachel-marke--ruht')) pille.remove();
      if (abzugMarken.querySelector('.dp-kachel-marke')) {
        // Ohne Einblenden: die Kopie steht vom ersten Bild an voll da (die Startregel liesse sie sonst von null kommen).
        for (const pille of abzugMarken.querySelectorAll('.dp-kachel-marke')) {
          if (pille instanceof HTMLElement) pille.style.transition = 'none';
        }
        abzugMarken.style.inset = 'auto';
        abzugMarken.style.top = markenRand.top - rand.top + 'px';
        abzugMarken.style.left = markenRand.left - rand.left + 'px';
        abzugMarken.style.width = markenRand.width + 'px';
        abzugMarken.style.margin = '0';
        kopie.appendChild(abzugMarken);
      }
    }
  }

  /*
   * Stand statt der Bilder der Hinweis "Noch keine Bilder" da (leere
   * Galerie, dp-galerie--leer), gehoert er ins Standbild - an seine
   * gemessene Stelle, sonst verschwaende er beim Wechsel schlagartig unter
   * dem deckenden Grund, statt mit dem Standbild auszublenden.
   */
  const hinweis = galerie.querySelector('.dp-ohne-bild--farbwahl');
  const hinweisRand = hinweis ? hinweis.getBoundingClientRect() : null;
  if (hinweis && hinweisRand && hinweisRand.width && hinweisRand.height) {
    const abzug = /** @type {HTMLElement} */ (hinweis.cloneNode(true));
    abzug.style.position = 'absolute';
    abzug.style.top = hinweisRand.top - rand.top + 'px';
    abzug.style.left = hinweisRand.left - rand.left + 'px';
    abzug.style.width = hinweisRand.width + 'px';
    abzug.style.height = hinweisRand.height + 'px';
    abzug.style.margin = '0';
    abzug.style.aspectRatio = 'auto';
    abzug.style.borderRadius = getComputedStyle(hinweis).borderRadius;
    // Die Galerie-Regel (display: grid nur in der leeren Galerie) greift ausserhalb nicht.
    abzug.style.display = 'grid';
    kopie.appendChild(abzug);
  }

  halter.appendChild(kopie);
  standbild = kopie;

  /*
   * Ein neuer Wechsel beginnt - die Sperre faellt, damit er seine eigenen
   * Nachzuegler zurueckhalten kann.
   *
   * Was vom vorigen Wechsel noch haengt, wird hier bewusst NICHT freigegeben:
   * das Theme meldet dieselbe Auswahl gelegentlich ein zweites Mal, und eine
   * Freigabe an dieser Stelle riss die Kacheln kurz vor den Fahrten aus dem
   * Halt. Ihre eigenen Wecker und das Netz weiter unten raeumen sie ab.
   */
  nachzueglerGeprueft = false;

  /*
   * Merkzettel am Wurzelelement: solange er steht, wird die Galerie gerade
   * ausgetauscht und ihre Hoehe ist nichts, worauf sich jemand verlassen
   * kann. Das Kauffenster wartet darauf, bevor es seinen Platz neu sucht
   * (dp-kauffenster.js) - sonst faehrt es zur Hoehe der halbfertigen
   * Galerie und gleich darauf wieder zurueck.
   */
  window.clearTimeout(wechselMarke);
  document.documentElement.dataset.dpGalerieWechselt = '';
  wechselMarke = window.setTimeout(loeseWechselmarke, MARKE_NETZ);

  // Kommt keine neue Galerie - etwa weil nur die Groesse wechselte und das
  // Theme nichts neu zeichnet -, verschwindet das Standbild von selbst.
  // Der Farbfilter laeuft trotzdem, damit die Auswahl nie veraltet.
  sicherung = window.setTimeout(() => {
    filtereNachFarbe(sichtbareGalerie());
    raeumeStandbildAb();
  }, 1500);
}

/*
 * Ein Wechsel in der Variantenauswahl kuendigt den Austausch an - das
 * Standbild muss vorher liegen, nachher gibt es die alten Bilder nicht mehr.
 */
document.addEventListener(
  'change',
  (ereignis) => {
    if (!(ereignis.target instanceof Element)) return;
    /*
     * Nur die Variantenwahl tauscht die Galerie. Frueher genuegte jede
     * Aenderung im Kauffenster - und damit auch jedes Eingabefeld: nach dem
     * Tippen oder einer Dropdown-Wahl lag das weisse Standbild anderthalb
     * Sekunden ueber der Galerie, ohne dass darunter etwas wechselte.
     */
    if (!ereignis.target.closest('variant-picker')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    legeStandbild();
  },
  true
);

/*
 * Sobald eine neue Galerie eingesetzt ist, blendet das Standbild aus. Der
 * Beobachter laeuft dauerhaft, tut aber nur waehrend eines Wechsels etwas.
 */
/**
 * Blendet ab, sobald das neue erste Bild wirklich zu sehen waere - also
 * geladen ist. Sonst gaebe die Blende den halbfertigen Ladezustand frei.
 *
 * @param {Element | null} galerie
 */
/*
 * Kacheln, die es bei der vorigen Farbe nicht gab.
 *
 * Sie fliegen nicht mit - sie stehen von Anfang an auf ihrem Platz. Bis die
 * uebrigen Kacheln ihre neue Groesse und Lage erreicht haben, sollen sie
 * weder zu sehen sein noch ihre Bilder holen: Das Standbild deckt die alte
 * Szene, reicht aber nur ueber deren Hoehe - eine laengere neue Galerie
 * lugte darunter hervor, und ihre Bilder erschienen hinter den noch
 * fahrenden Kacheln, um danach an ihren Platz zu springen.
 */
let nachzuegler = [];
let nachzueglerNetz = 0;

/*
 * Je Wechsel nur einmal gehalten.
 *
 * Der Beobachter unten horcht auch auf src und srcset - genau die Angaben,
 * die hier abgenommen und spaeter zurueckgegeben werden. Ohne diese Sperre
 * riefe jede Abnahme den Beobachter, der Beobachter wieder diese Funktion,
 * und die Seite stuende still.
 *
 * Bewusst je Wechsel und nicht je Galerie-Element: das Theme tauscht die
 * Bilder oft an Ort und Stelle aus, das Element bleibt dann dasselbe - eine
 * Sperre am Element haette beim naechsten Farbwechsel nicht mehr gegriffen.
 * Zurueckgesetzt wird sie in legeStandbild, also zu Beginn jedes Wechsels.
 */
let nachzueglerGeprueft = false;

/** Dauer der Blende, mit der ein Nachzuegler erscheint. */
const NACHZUEGLER_BLENDE = 260;

/**
 * Nimmt die hinzugekommenen Kacheln aus dem Bild - samt ihrer Bildquellen.
 *
 * @param {Element} galerie
 */
function haltNachzuegler(galerie) {
  if (!galerie || nachzueglerGeprueft) return;
  if (!alteLagen.size) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /*
   * Erst suchen, dann zugreifen. Diese Funktion laeuft nach jedem
   * Filterlauf, und in den ersten Durchgaengen steht oft noch die alte
   * Galerie da - dort gibt es keine Nachzuegler. Wuerde die Sperre schon
   * dabei fallen, kaeme die eigentliche Gelegenheit nie.
   */
  const gefunden = [];
  for (const { kachel, schluessel } of plaetzeVon(galerie)) {
    if (!(kachel instanceof HTMLElement)) continue;
    // Die 3D-Kachel bekommt ihre Buehne sofort (dp-modell3d.js) - sie wird nie zurueckgehalten.
    if (kachel.classList.contains('dp-modell3d')) continue;

    /*
     * Nur was wirklich Masse hat: das Theme haelt neben dem sichtbaren
     * Raster noch zwei Fassungen derselben Kacheln vor (eine Vorlage und
     * die Grossansicht), und die haben keine Hoehe.
     */
    if (!bildkastenVon(kachel).getBoundingClientRect().height) continue;
    if (alteLagen.has(schluessel)) continue;

    gefunden.push(kachel);
  }

  if (!gefunden.length) return;
  nachzueglerGeprueft = true;

  for (const kachel of gefunden) {
    kachel.style.transition = 'none';
    kachel.style.opacity = '0';

    /*
     * Die Quellen kommen weg, damit der Browser gar nicht erst zu laden
     * beginnt. Ohne sie behaelt die Kachel ihre Masse - die kommen vom
     * Seitenverhaeltnis des Medienkastens, nicht vom Bild.
     */
    const bilder = [];
    for (const bild of kachel.querySelectorAll('img')) {
      bilder.push({
        bild,
        quelle: bild.getAttribute('src') || '',
        satz: bild.getAttribute('srcset') || '',
      });
      bild.removeAttribute('src');
      bild.removeAttribute('srcset');
    }

    nachzuegler.push({ kachel, bilder });
  }

  /*
   * Ab jetzt wird auf die Fahrten gewartet - und nur auf sie.
   *
   * Waehrend eines Wechsels laufen mehrere Durchgaenge, und mehr als einer
   * davon meldete frueher "fertig", bevor die Fahrten ueberhaupt begonnen
   * hatten. Die Kacheln kamen dann kurz vor ihnen ins Bild, und man sah
   * genau das, was hier vermieden werden soll. Freigegeben wird deshalb
   * erst, wenn die Fahrten abgeraeumt sind - oder, wenn keine kommen, vom
   * Netz darunter.
   */
  nachzueglerWartet = true;

  window.clearTimeout(nachzueglerNetz);
  nachzueglerNetz = window.setTimeout(() => gibNachzueglerFrei(true), 2400);
}

/** Wecker fuer die Freigabe - jeder Durchgang stellt ihn neu. */
let nachzueglerWecker = 0;

/** Solange wahr, wartet die Freigabe auf das Ende der Fahrten. */
let nachzueglerWartet = false;

/*
 * Wie viele Kachelfahrten gerade laufen.
 *
 * Ein Wechsel bringt oft mehr als eine Runde: ein frueher Durchgang misst
 * eine Geometrie, die noch nicht steht, und die eigentliche Runde folgt
 * kurz darauf. Freigegeben wird erst, wenn die letzte davon abgeraeumt ist -
 * ein Zaehler ist dafuer verlaesslicher als jeder einzelne Wecker.
 */
let fahrtenOffen = 0;

/** So lange muss nach einer Kachelfahrt Ruhe sein, bevor freigegeben wird. */
const RUHE_NACH_FAHRT = 260;

/**
 * Stellt die Freigabe auf spaeter - und schiebt eine bereits geplante nach
 * hinten.
 *
 * @param {number} frist
 */
function planeNachzueglerFreigabe(frist) {
  window.clearTimeout(nachzueglerWecker);
  nachzueglerWecker = window.setTimeout(() => gibNachzueglerFrei(true), frist);
}

/**
 * Gibt die zurueckgehaltenen Kacheln frei: erst die Bildquellen, dann - wenn
 * das Bild steht - die Blende.
 *
 * @param {boolean} [erzwungen] Vom Netz gerufen: dann wird nicht mehr auf
 *   die Fahrten gewartet.
 */
function gibNachzueglerFrei(erzwungen) {
  window.clearTimeout(nachzueglerWecker);

  /*
   * Noch laufen die Fahrten - dann nicht. Angestossen wird die Freigabe
   * ausschliesslich vom Wecker nach der letzten Fahrt und vom Netz; jeder
   * andere Aufruf prallt hier ab. Frueher gaben mehrere Stellen frei, und
   * eine davon war jedes Mal zu frueh dran.
   */
  if (nachzueglerWartet && erzwungen !== true) return;

  nachzueglerWartet = false;
  window.clearTimeout(nachzueglerNetz);
  const liste = nachzuegler;
  nachzuegler = [];

  for (const { kachel, bilder } of liste) {
    for (const { bild, quelle, satz } of bilder) {
      if (satz) bild.setAttribute('srcset', satz);
      if (quelle) bild.setAttribute('src', quelle);
    }

    const zeigen = () => {
      if (!kachel.isConnected) return;
      kachel.style.transition = 'opacity ' + NACHZUEGLER_BLENDE + 'ms ease-out';
      kachel.style.opacity = '1';
      window.setTimeout(() => {
        kachel.style.removeProperty('transition');
        kachel.style.removeProperty('opacity');
      }, NACHZUEGLER_BLENDE + 60);
    };

    /*
     * Gewartet wird auf das Bild selbst: eine leere Kachel einzublenden und
     * das Bild darin nachtraeglich erscheinen zu lassen waeren zwei
     * Vorgaenge, wo einer genuegt. Die Frist darunter sorgt dafuer, dass ein
     * zaehes Bild die Kachel nicht dauerhaft verbirgt.
     */
    const bild = bilder[0] && bilder[0].bild;
    if (!bild || bild.complete) {
      zeigen();
      continue;
    }

    let erledigt = false;
    const einmal = () => {
      if (erledigt) return;
      erledigt = true;
      zeigen();
    };
    bild.addEventListener('load', einmal, { once: true });
    bild.addEventListener('error', einmal, { once: true });
    window.setTimeout(einmal, 4000);
  }
}

/**
 * Ob der dichte Satz (dp-galerie-satz.js) an der Galerie schon steht: das
 * Raster traegt seine Klasse, und jede sichtbare Kachel ihre Zeilenspanne.
 *
 * @param {Element} galerie
 * @returns {boolean}
 */
function satzSteht(galerie) {
  const gitter = galerie.querySelector('.media-gallery__grid');
  if (!(gitter instanceof HTMLElement)) return true;
  // Wo das Raster kein Raster ist (Rechner), gibt es keinen Satz abzuwarten.
  if (getComputedStyle(gitter).display !== 'grid') return true;
  if (!gitter.classList.contains('dp-galerie-satz')) return false;
  for (const kachel of gitter.children) {
    if (!(kachel instanceof HTMLElement)) continue;
    if (getComputedStyle(kachel).display === 'none') continue;
    if (!kachel.dataset.dpSpanne) return false;
  }
  return true;
}

/**
 * Wartet, bis der dichte Satz steht - hoechstens ein paar Bilder lang.
 *
 * Die Ziele der Fahrten werden an der neuen Galerie gemessen. Der Satz
 * (Spalte und Zeilenspanne je Kachel) kommt aber aus einem eigenen Skript,
 * das nach dem Einsetzen der Galerie ueber einen Zeitgeber anlaeuft - und
 * auf dem iPhone kam die Messung hier regelmaessig vor diesem Lauf. Gemessen
 * wurde dann das ungesetzte Raster, in dem jede Zeile so hoch ist wie ihre
 * hoechste Kachel: die dritte Kachel fuhr an einen Platz mit Luft unter der
 * ersten und sprang nach der Fahrt an ihren wirklichen Platz direkt
 * darunter. Auf Android lief der Satz zufaellig frueher, dort fiel nichts auf.
 *
 * @param {Element} galerie
 * @param {() => void} dann
 */
function wennSatzSteht(galerie, dann) {
  let versuche = 0;
  const pruefe = () => {
    if (satzSteht(galerie) || versuche > 20) {
      dann();
      return;
    }
    versuche += 1;
    requestAnimationFrame(pruefe);
  };
  pruefe();
}

function raeumeAbSobaldBereit(galerie) {
  /*
   * Zuerst filtern - erst danach wird die neue Geometrie fuer die Fahrten
   * gemessen. So gilt die Farbauswahl schon fuer die Ziele der Geister.
   */
  filtereNachFarbe(galerie);

  /*
   * Gleich hier, nicht erst mit den Fahrten: zwischen dem Einsetzen der
   * neuen Galerie und dem Start der Fahrten liegen mehrere Bilder, und in
   * denen waren die hinzugekommenen Kacheln zu sehen.
   */
  haltNachzuegler(galerie);

  /*
   * Gewartet wird auf das erste Bild, das auch zu sehen ist. Das erste im
   * Markup kann vom Farbfilter verborgen sein - es laedt dann nie, und die
   * Blende hinge bis zum Netz unten.
   */
  const bild = erstesSichtbaresBild(galerie);

  const frei = () =>
    wennSatzSteht(galerie, () => {
      lasseKachelnGleiten(galerie);
      raeumeStandbildAb();
    });

  if (!bild || bild.complete) {
    requestAnimationFrame(() => requestAnimationFrame(frei));
    return;
  }

  bild.addEventListener('load', frei, { once: true });
  bild.addEventListener('error', frei, { once: true });

  /*
   * Netz unter dem Netz: laedt das Bild zaeh, faellt die Blende trotzdem.
   *
   * Die Frist war mit 1,2 Sekunden zu knapp. Auf einer langsameren
   * Verbindung war das neue Bild da noch unterwegs: Das Standbild ging
   * weg, die Kachel stand einen Moment leer, und das Bild blendete
   * anschliessend einzeln auf (dp-bildblende.js) - statt der weichen
   * Ueberblendung sah man einen harten Schnitt mit Loch dazwischen. Genau
   * das war zu beobachten. Vier Sekunden decken auch eine traege
   * Verbindung ab; laenger soll niemand auf ein Standbild schauen.
   */
  window.setTimeout(frei, 4000);
}

/**
 * Laesst jede veraenderte Kachel von ihrer alten Lage in die neue gleiten -
 * Ort und Hoehe gemeinsam, in einer Bewegung.
 *
 * Gefahren wird mit Geistern: fuer jede Kachel, deren Lage oder Hoehe sich
 * geaendert hat, entsteht eine freie Kopie der neuen Kachel, die an der
 * alten Geometrie startet und in einem Zug auf die neue faehrt - Ort und
 * Hoehe mit derselben Dauer und derselben Kurve. Alles bewegt sich also im
 * Gleichschritt, nichts laeuft schneller als das Uebrige, und weil die
 * Geister ausserhalb des Spaltensatzes leben, kann nichts mitten im Weg die
 * Spalte wechseln oder sich stapeln.
 *
 * Die echten Kacheln stehen waehrenddessen unsichtbar an ihren Endplaetzen
 * und erscheinen erst, wenn ihr Geist gelandet ist. Kacheln mit unveraenderter
 * Lage bleiben schlicht stehen.
 *
 * Traegt der Geist dasselbe Bild wie vorher, wird die Kopie im Standbild
 * sofort unsichtbar - er schwebt offen. Traegt er ein neues Bild (das erste
 * Bild nach dem Farbwechsel), blendet das Standbild wie gehabt darueber aus
 * und deckt den Inhaltswechsel; die Geometrie faehrt darunter mit.
 *
 * @param {Element | null} galerie
 */
function lasseKachelnGleiten(galerie) {
  if (!galerie) return;

  /*
   * Keine alten Lagen heisst hier zweierlei: entweder gab es nie welche -
   * dann haelt auch niemand etwas zurueck -, oder die Fahrten sind bereits
   * gestartet und haben sie verbraucht. Der Beobachter ruft diese Funktion
   * mehrfach; ein Freigeben an dieser Stelle riss die zurueckgehaltenen
   * Kacheln mitten aus der laufenden Fahrt heraus.
   */
  if (!alteLagen.size) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const halter = galerie.closest('.shopify-section') || galerie.parentElement;
  if (!(halter instanceof HTMLElement)) return;
  if (getComputedStyle(halter).position === 'static') halter.style.position = 'relative';

  const halterRand = halter.getBoundingClientRect();
  const halterOben = halterRand.top + window.scrollY;
  const halterLinks = halterRand.left + window.scrollX;

  const DAUER = 300;

  const klonPlaetze = standbild ? standbildTeile : null;

  const fahrten = [];

  for (const { kachel, schluessel } of plaetzeVon(galerie)) {
    if (!(kachel instanceof HTMLElement)) continue;
    // Die 3D-Kachel faehrt nicht als Kopie: ihre Kopie waere eine leere Szene ohne Modell.
    if (kachel.classList.contains('dp-modell3d')) continue;

    const rand = bildkastenVon(kachel).getBoundingClientRect();
    if (!rand.height) continue;

    /*
     * Die hinzugekommenen Kacheln liegen schon zurueck (haltNachzuegler) -
     * hier gibt es fuer sie nichts zu tun.
     */
    const alt = alteLagen.get(schluessel);
    if (!alt) continue;

    const neuOben = rand.top + window.scrollY;
    const neuLinks = rand.left + window.scrollX;

    const stillsteht =
      Math.abs(alt.links - neuLinks) < 2 &&
      Math.abs(alt.oben - neuOben) < 2 &&
      Math.abs(alt.hoehe - rand.height) < 3;
    if (stillsteht) continue;

    const bild = kachel.querySelector('img');
    const quelle = bild ? (bild.currentSrc || bild.src).split('?')[0] : '';
    const gleichesBild = quelle && quelle === alt.quelle;

    /* Der Geist: eine Kopie der neuen Kachel, frei beweglich. */
    const geist = /** @type {HTMLElement} */ (kachel.cloneNode(true));
    geist.setAttribute('aria-hidden', 'true');
    // Kopierte Zeichenflaechen sind leer, belegen aber Speicher - gleich frei.
    gibLeinwaendeFrei(geist);
    geist.removeAttribute('id');
    for (const el of geist.querySelectorAll('[id]')) el.removeAttribute('id');
    // Die Ladeblende gilt dem Original - der Geist muss sofort zu sehen sein.
    for (const el of geist.querySelectorAll('.dp-blende')) el.classList.remove('dp-blende');

    geist.style.position = 'absolute';
    geist.style.top = alt.oben - halterOben + 'px';
    geist.style.left = alt.links - halterLinks + 'px';
    geist.style.width = rand.width + 'px';
    geist.style.height = alt.hoehe + 'px';
    geist.style.margin = '0';
    // Der Haarstrich der Galeriebilder - siehe den Schnappschuss oben.
    geist.style.outline = '1px solid ' + haarstrichFarbe();
    geist.style.outlineOffset = '-1px';
    /*
     * "hidden" statt "clip": aeltere iOS-Fassungen kennen clip nicht, die
     * Angabe verfiel dort ersatzlos - und der Geist beschnitt gar nicht mehr.
     * Sein Bild ragte dann waehrend der Fahrt ueber den Kachelrand und legte
     * sich ueber die Nachbarn.
     */
    geist.style.overflow = 'hidden';
    geist.style.pointerEvents = 'none';
    geist.style.transition = 'none';
    /*
     * Jeder Geist fliegt ueber dem Standbild. Die fruehe Fassung liess
     * Geister mit neuem Bild darunter fahren, damit die Standbild-Blende den
     * Inhaltswechsel deckt - aber unterhalb des Standbilds lugten sie in
     * dessen Fugen hervor: unter den alten Bildern blitzten die neuen durch.
     * Den Inhaltswechsel deckt stattdessen der Alt-Bild-Geist (z 4), der
     * obenauf mitfaehrt und ausblendet.
     */
    geist.style.zIndex = '3';

    /*
     * Die Rundung faehrt mit: sie sitzt im Original auf dem Medienkasten,
     * beim Geist muss sie an den beschneidenden Aussenkasten - sonst fuhr
     * eine eckige Kachel durchs Bild.
     */
    const medienOriginal = kachel.querySelector('.product-media');
    if (medienOriginal) {
      geist.style.borderRadius = getComputedStyle(medienOriginal).borderRadius;
    }

    /*
     * Waehrend der Fahrt fuellt das Bild den Geist vollstaendig und wird
     * beschnitten. Wichtig ist das Abschalten der Seitenverhaeltnisse auf
     * dem ganzen Weg nach innen: der Medienkasten traegt ein aspect-ratio,
     * und zusammen mit einer festen Hoehe rechnete der Browser daraus eine
     * eigene Breite - das Bild wuchs seitlich aus der Kachel heraus.
     */
    for (const teil of geist.querySelectorAll(
      '.product-media, .product-media__drag-zoom-wrapper, .product-media img'
    )) {
      if (!(teil instanceof HTMLElement)) continue;
      teil.style.aspectRatio = 'auto';
      teil.style.height = '100%';
      teil.style.width = '100%';
      teil.style.minHeight = '0';
      teil.style.maxHeight = 'none';
      teil.style.borderRadius = '0';
      if (teil instanceof HTMLImageElement) {
        teil.style.objectFit = 'cover';
        // Wie beim Standbild: Safari zeichnet einen Klon sonst erst verzoegert - weisser Blitz.
        teil.decoding = 'sync';
        teil.loading = 'eager';
      }
    }

    halter.appendChild(geist);
    kachel.style.visibility = 'hidden';

    /*
     * Der Zwilling im Standbild wird immer ausgeblendet: seine Rolle
     * uebernimmt ab hier ein Geist. Bei gleichem Bild reicht der eine (die
     * Kachel schwebt offen); bei neuem Bild kommt ein zweiter mit dem alten
     * Bild obendrauf - er faehrt dieselbe Strecke und dieselbe
     * Groessenaenderung mit und blendet dabei aus. Das alte Bild nimmt die
     * neue Kachelform also noch an, waehrend es verschwindet - erst dadurch
     * lesen sich Form- und Bildwechsel als ein einziger Vorgang.
     */
    let altGeist = null;
    if (klonPlaetze) {
      const doppel = klonPlaetze.get(schluessel);
      if (doppel instanceof HTMLElement) {
        if (!gleichesBild) {
          // Der Abzug aus dem Standbild ist bereits ein fertig gestyltes
          // Einzelbild - fuer die Fahrt genuegt eine Kopie davon.
          altGeist = /** @type {HTMLElement} */ (doppel.cloneNode(true));
          if (altGeist instanceof HTMLImageElement) {
            altGeist.decoding = 'sync';
            altGeist.loading = 'eager';
          }
          altGeist.style.position = 'absolute';
          altGeist.style.top = alt.oben - halterOben + 'px';
          altGeist.style.left = alt.links - halterLinks + 'px';
          altGeist.style.width = rand.width + 'px';
          altGeist.style.height = alt.hoehe + 'px';
          altGeist.style.margin = '0';
          altGeist.style.outline = '1px solid ' + haarstrichFarbe();
          altGeist.style.outlineOffset = '-1px';
          altGeist.style.pointerEvents = 'none';
          altGeist.style.transition = 'none';
          altGeist.style.opacity = '1';
          altGeist.style.zIndex = '4';

          halter.appendChild(altGeist);
        }
        doppel.style.visibility = 'hidden';
      }
    }

    fahrten.push({
      geist,
      altGeist,
      kachel,
      ziel: {
        top: neuOben - halterOben + 'px',
        left: neuLinks - halterLinks + 'px',
        height: rand.height + 'px',
      },
    });
  }

  alteLagen = new Map();

  /*
   * Ohne Fahrt wird hier nichts freigegeben: dieser Durchgang kann einer von
   * mehreren sein, und die eigentlichen Fahrten kommen vielleicht gleich
   * noch. Ein Wecker an dieser Stelle lief ihnen davon - die Kacheln kamen
   * kurz vor den Fahrten ins Bild. Die Freigabe haengt am Abraeumen des
   * Standbilds, also am Ende des Wechsels.
   */
  if (!fahrten.length) {
    // Nichts zu fahren heisst: die Kacheln stehen schon. Das Kauffenster
    // darf sich sofort ausrichten.
    loeseWechselmarke();
    return;
  }

  // Erst stehen alle an ihren alten Orten - dann faehrt alles gemeinsam los.
  void halter.getBoundingClientRect();

  requestAnimationFrame(() => {
    for (const { geist, altGeist, ziel } of fahrten) {
      const weg =
        'top ' + DAUER + 'ms ease-out, left ' + DAUER + 'ms ease-out, height ' + DAUER + 'ms ease-out';

      geist.style.transition = weg;
      geist.style.top = ziel.top;
      geist.style.left = ziel.left;
      geist.style.height = ziel.height;

      if (altGeist) {
        altGeist.style.transition = weg + ', opacity 260ms ease-out';
        altGeist.style.top = ziel.top;
        altGeist.style.left = ziel.left;
        altGeist.style.height = ziel.height;
        altGeist.style.opacity = '0';
      }
    }
  });

  window.clearTimeout(nachzueglerWecker);
  fahrtenOffen += 1;

  window.setTimeout(() => {
    for (const { geist, altGeist, kachel } of fahrten) {
      kachel.style.visibility = '';
      geist.remove();
      gibLeinwaendeFrei(geist, true);
      if (altGeist) {
        altGeist.remove();
        gibLeinwaendeFrei(altGeist, true);
      }
    }

    /*
     * Und dann die hinzugekommenen Kacheln - aber erst nach einer kurzen
     * Ruhe. Ein Wechsel bringt oft mehr als eine Runde: ein frueher
     * Durchgang misst eine Geometrie, die noch nicht steht, die eigentliche
     * Runde folgt kurz darauf. Wer nach der ersten freigibt, zeigt die
     * Kacheln, kurz bevor sich alles noch einmal bewegt. Jede neue Runde
     * loescht diesen Wecker oben wieder.
     */
    /*
     * Jetzt stehen Groesse und Lage der Kacheln fest - das Kauffenster darf
     * seinen neuen Platz einnehmen. Auf die Bilder in den Kacheln wartet es
     * bewusst nicht: die aendern an der Geometrie nichts mehr.
     */
    loeseWechselmarke();

    fahrtenOffen = Math.max(0, fahrtenOffen - 1);
    planeNachzueglerFreigabe(RUHE_NACH_FAHRT);
  }, DAUER + 80);
}

new MutationObserver(() => {
  /*
   * Unabhaengig vom Standbild: nach jeder Aenderung im Dokument wird der
   * Farbfilter fuer die naechste Bildwiederholung eingeplant. Er ist billig,
   * greift nur, wenn wirklich etwas abweicht, und faengt so auch die Galerie
   * ab, die eine verspaetete Antwort nach dem Abraeumen noch einsetzt.
   */
  planeFilter();

  if (!standbild) return;

  /*
   * Solange die hinzugekommenen Kacheln noch nicht zurueckgehalten sind,
   * geschieht das hier sofort - nicht erst mit dem eingeplanten Filterlauf.
   *
   * Der Beobachter meldet sich vor dem naechsten Zeichnen; ein Filterlauf
   * auf der naechsten Bildwiederholung ist ein Bild spaeter. Genau dieses
   * eine Bild zeigte die neue Galerie in voller Laenge, und was ueber das
   * Standbild hinausragte, war zu sehen: ein schwarzes Shirt unter einer
   * gruenen Kachel. Gefiltert werden muss dabei mit, denn die Zuordnung der
   * Plaetze zaehlt nur die sichtbaren Kacheln.
   */
  if (!nachzueglerGeprueft) {
    const sicht = sichtbareGalerie();
    filtereNachFarbe(sicht);
    haltNachzuegler(sicht);
  }

  /*
   * Zwei Arten von Wechsel: das Theme setzt eine wirklich neue Galerie ein -
   * oder es tauscht die Bilder der bestehenden an Ort und Stelle (dann bleibt
   * das Element dasselbe und nur die Quelle des ersten Bildes aendert sich).
   * Beides heisst: unter dem Standbild steht jetzt das Neue.
   *
   * Bewusst nicht bei jeder beliebigen Aenderung im Dokument: auf dem Telefon
   * ist der Nachschub laenger unterwegs, und dazwischen mutiert immer etwas -
   * die erste Fassung raeumte das Standbild deshalb zu frueh ab, und der
   * Wechsel war wieder hart.
   */
  /*
   * Gefragt ist die Galerie, die der Besucher vor sich hat - nicht
   * irgendeine andere im Dokument.
   *
   * Frueher stand hier "die erste, die nicht die alte ist". Auf einem
   * Geraet lagen vier media-gallery-Elemente gleichzeitig im Dokument
   * (Fassungen fuer verschiedene Breiten). Gefunden wurde dann eine
   * unsichtbare, die schon die ganze Zeit dort lag: Ihr Bild war laengst
   * geladen, das Standbild flog sofort weg - der Wechsel war hart -, und
   * die Kachelfahrten rechneten gegen eine Galerie, die niemand sieht.
   * Deshalb fuhr keine einzige Kachel.
   */
  const neue = sichtbareGalerie();

  if (neue && neue !== alteGalerie && neue !== standbild) {
    /*
     * Die alte Galerie ist ersetzt und aus dem Dokument: ihre Bilder geben den
     * Speicher ab (Speicherspur am iPhone, Oktober 2026). Nur Bilder - die
     * Buehne der 3D-Kachel zieht gleich in die neue Galerie (dp-modell3d.js).
     */
    const abgeloest = alteGalerie;
    // Etwas spaeter - erst uebernehmen Grossansicht und Kacheln die neue Galerie.
    if (abgeloest && !abgeloest.isConnected) {
      window.setTimeout(() => {
        if (abgeloest.isConnected) return;
        for (const b of abgeloest.querySelectorAll('img')) {
          if (b.closest('.dp-modell3d__buehne')) continue;
          b.removeAttribute('srcset');
          b.removeAttribute('src');
        }
      }, 2000);
    }
    raeumeAbSobaldBereit(neue);
    return;
  }

  if (alteGalerie && alteGalerie.isConnected) {
    const erstes = alteGalerie.querySelector('.product-media img');
    const quelle = erstes ? erstes.currentSrc || erstes.src : '';
    if (quelle && quelle !== alteQuelle) {
      raeumeAbSobaldBereit(alteGalerie);
    }
  }
}).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });


/*
 * Der Farbfilter greift gleich beim Seitenaufbau - nicht erst beim ersten
 * Wechsel, sonst zeigte die frisch geladene Seite noch alle Farben.
 */
function filtereBeimStart() {
  filtereNachFarbe(sichtbareGalerie());
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', filtereBeimStart, { once: true });
} else {
  filtereBeimStart();
}

/* ------------------------------------- Ladering an der gewaehlten Farbe */

/*
 * Solange die Bilder einer neu gewaehlten Farbe unterwegs sind, dreht sich
 * ein Bogen um ihren Farbkreis. Die Gestaltung steht im Stilblatt; hier
 * wird nur entschieden, wann der Zustand kommt und wann er wieder geht.
 *
 * Gemerkt wird er am Wurzelelement und nicht an einem einzelnen Etikett:
 * das Theme zeichnet die Farbauswahl beim Wechsel neu, und eine Klasse an
 * einem Etikett verschwaende mit ihm. Welcher Kreis der gewaehlte ist,
 * findet das Stilblatt selbst.
 *
 * Fertig heisst: der Austausch ist vorbei, und jedes Bild der sichtbaren
 * Galerie in Reichweite des Sichtbereichs traegt sein Bild. Was weit
 * unten steht, laedt ohnehin erst beim Hinscrollen - darauf soll niemand
 * warten.
 */
const RING_TAKT = 140;
const RING_HOECHSTENS = 12000;

let ringWecker = 0;

function ringWeg() {
  window.clearTimeout(ringWecker);
  delete document.documentElement.dataset.dpFarbeLaedt;
}

/** Ob die Bilder der Galerie in Reichweite des Sichtbereichs geladen sind. */
function bilderStehen() {
  const galerie = sichtbareGalerie();
  if (!galerie) return true;

  const grenze = window.innerHeight * 2;
  for (const bild of galerie.querySelectorAll('.media-gallery__grid img')) {
    if (!bild.getAttribute('src')) continue;
    // Vom Farbfilter verborgene Kacheln laden nie - auf sie wartet niemand.
    const kachel = bild.closest('.product-media-container');
    if (kachel instanceof HTMLElement && kachel.style.display === 'none') continue;
    if (bild.complete) continue;
    if (bild.getBoundingClientRect().top > grenze) continue;
    return false;
  }
  return true;
}

function ringAn() {
  document.documentElement.dataset.dpFarbeLaedt = '';

  const bis = performance.now() + RING_HOECHSTENS;
  const sieheNach = () => {
    if (performance.now() > bis) return ringWeg();

    /*
     * Solange der Austausch laeuft, steht noch die alte Galerie da - deren
     * Bilder sind laengst geladen und saehen faelschlich nach "fertig"
     * aus. Gezaehlt wird erst, wenn die neue Strecke steht.
     */
    const wechselt = document.documentElement.dataset.dpGalerieWechselt !== undefined;
    if (!wechselt && bilderStehen()) return ringWeg();

    ringWecker = window.setTimeout(sieheNach, RING_TAKT);
  };

  window.clearTimeout(ringWecker);
  ringWecker = window.setTimeout(sieheNach, RING_TAKT);
}

/*
 * Angestossen wird der Ring von derselben Auswahl, die auch das Standbild
 * legt - in der Einfangphase, damit er steht, bevor das Theme neu zeichnet.
 */
document.addEventListener(
  'change',
  (ereignis) => {
    const ziel = ereignis.target;
    if (!(ziel instanceof Element) || !ziel.closest('.dp-kauffenster variant-picker')) return;
    ringAn();
  },
  true
);
